import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, watch, getCurrentInstance } from "vue";
import axios from "@/axios-auth";
import router from "@/router";
import { showAlert, confirmModal } from "@/utils/toast";
import moment from "moment";
import { debounce } from "lodash";
import { getCategoriasConfig, getDeportesConfig } from "@/utils/models";
import ListaInscriptos from "./ListaInscriptos.vue";
import ModalFinalizarCompetencia from "./ModalFinalizarCompetencia.vue";
import type { JugadorParaFinalizar } from "@/utils/rankings";


export default /*#__PURE__*/_defineComponent({
  __name: 'ListaCompeticiones',
  setup(__props) {

const { proxy } = getCurrentInstance();

const menu = ref(false);
const fechaFormateada = ref("");

// Estado
const modalFinalizarVisible = ref(false);
const jugadoresFinales = ref<JugadorParaFinalizar[]>([]);
const idRanking = ref<Number | undefined>(undefined);

const cargando = ref(false);
const torneos = ref<any[]>([]);
const filtros = ref({
  buscar: "",
  estado: "Todos",
  genero: "Todos",
  categoria: "Todos",
  sistemaCompetencia: "Todos",
  deporte: "Todos",
  fecha: ["", ""],
});
const opcionesTabla = ref({
  page: 1,
  itemsPerPage: 10,
});
const cantRegistros = ref();
const valorInscripcionRules = ref([
  (v) => v >= 0 || "El total debe ser mayor o igual a 0",
]);

// Modal nuevo torneo
const modalNuevoVisible = ref(false);
const modalInscripciones = ref(false);
const formValido = ref(false);
const guardando = ref(false);
const torneoEditando = ref<any>(null);
const nuevoTorneo = ref<{
  nombre: string;
  descripcion: string;
  genero: string | null;
  categoria: string | null;
  fecha: string | null;
  tipoCompetencia: string | null;
  valorInscripcion: string | number | null;
  sistemaCompetencia: string | null;
  deporte: string | null;
}>({
  nombre: "",
  descripcion: "",
  genero: null,
  categoria: null,
  fecha: null,
  tipoCompetencia: null,
  valorInscripcion: "",
  sistemaCompetencia: null,
  deporte: null,
});
const editedItem = ref({
  idTorneo: null,
  infoTorneo: "",
  sistemaCompetencia: "",
});

const editedItemFinaliza = ref({
  idTorneo: null,
  nombreTorneo: "",
  tipoCompetencia: undefined,
  categoriaTorneo: "",
  generoTorneo: "",
  deporte: null,
});

// Configuraciones
const estados = [
  { text: "Draft", value: "Draft" },
  { text: "Publicado", value: "Publicado" },
  { text: "Finalizado", value: "Finalizado" },
];

const sistemas = [
  { text: "Torneo", value: "Torneo" },
  { text: "Liga", value: "Liga" },
];

const sistemasFiltros = [{ text: "Todos", value: "Todos" }, ...sistemas];

const estadosFiltros = [{ text: "Todos", value: "Todos" }, ...estados];

const generos = [
  { text: "Masculino", value: "Masculino" },
  { text: "Femenino", value: "Femenino" },
  { text: "Mixto", value: "Mixto" },
];

const generosFiltros = [{ text: "Todos", value: "Todos" }, ...generos];

const tiposCompetencia = [
  { text: "Dobles", value: "Dobles" },
  { text: "Single", value: "Single" },
];

const categoriasModel = computed(() => {
  const categorias = getCategoriasConfig(
    proxy.$store.state.usuario.institucion.idInstitucion,
  );
  return [...categorias.categorias, ...categorias.categoriasSuma];
});

const categoriasFiltros = [
  { text: "Todos", value: "Todos" },
  ...categoriasModel.value,
];

const deportesModel = computed(() => {
  return getDeportesConfig(proxy.$store.state.usuario.institucion.idInstitucion)
    .deportes;
});

const deportesFiltros = [
  { text: "Todos", value: "Todos" },
  ...deportesModel.value,
];

const headers = [
  { text: "Nombre", value: "nombre", sortable: true },
  { text: "Descripción", value: "descripcion", sortable: true },
  { text: "Deporte", value: "deporte", sortable: true },
  { text: "Sistema Competencia", value: "sistemaCompetencia", sortable: true },
  { text: "Tipo Comp.", value: "tipoCompetencia", sortable: true },
  { text: "Genero", value: "genero", sortable: true },
  { text: "Categoría", value: "categoria", sortable: true },
  { text: "Estado", value: "estado", sortable: true },
  { text: "Fecha Inicio", value: "fechaString", sortable: true },
  { text: "Acciones", value: "acciones", sortable: false, align: "start" },
];

const footerProps = {
  itemsPerPageOptions: [5, 10, 25, 50],
  showFirstLastPage: true,
  showCurrentPage: true,
  itemsPerPageText: "Competiciones por página",
};

// Snackbar
const snackbar = ref({
  visible: false,
  mensaje: "",
  color: "success",
});

// Computed
const filtrosActivos = computed(() => {
  return (
    filtros.value.buscar ||
    filtros.value.estado ||
    filtros.value.genero ||
    filtros.value.categoria ||
    filtros.value.deporte
  );
});

// Métodos

function volver() {
  proxy.$router.push("Home");
}

function toFixed() {
  if (isNaN(parseFloat(`${nuevoTorneo.value.valorInscripcion}`)))
    nuevoTorneo.value.valorInscripcion = parseFloat(`${0.0}`).toFixed(2);
  else
    nuevoTorneo.value.valorInscripcion = parseFloat(
      nuevoTorneo.value.valorInscripcion?.toString()!,
    ).toFixed(2);
}
function onlyNumber($event: any) {
  let keyCode = $event.keyCode ? $event.keyCode : $event.which;
  if ((keyCode < 48 || keyCode > 57) && keyCode != 46) {
    // 46 is dot
    $event.preventDefault();
  }
}

async function cargarTorneos() {
  try {
    cargando.value = true;

    const params = {
      idInstitucion: proxy.$store.state.usuario.institucion.idInstitucion,
      fechaDesde: filtros.value.fecha[0],
      fechaHasta: filtros.value.fecha[1],
      ...filtros.value,
      page: opcionesTabla.value.page,
      items: opcionesTabla.value.itemsPerPage,
    };

    const response = await axios.get("/Torneos/GetTorneos", { params });

    if (response.data) {
      torneos.value = response.data.torneos;
      cantRegistros.value = response.data.cantRegistros;
      console.log("torneos cargados", torneos.value);
    } else {
      torneos.value = [];
    }
  } catch (error) {
    showAlert({
      icon: "warning",
      title:
        "No se pudieron recuperar las competiciones. Intente nuevamente más tarde por favor.",
      timer: 2500,
      vm: proxy,
    });
    console.error(error);
  } finally {
    cargando.value = false;
  }
}

const onFiltroChangeDebounced = debounce(() => {
  cargarTorneos();
}, 500);

function onFiltroChange() {
  // Resetear a primera página cuando se cambian filtros
  opcionesTabla.value.page = 1;
  onFiltroChangeDebounced();
}

function quitarFiltro(filtro: string) {
  filtros.value[filtro as keyof typeof filtros.value] = "";
  onFiltroChange();
}

function limpiarFiltros() {
  setearFechaInicial();
  filtros.value = {
    ...filtros.value,
    buscar: "",
    estado: "Todos",
    genero: "Todos",
    categoria: "Todos",
    sistemaCompetencia: "Todos",
    deporte: "Todos",
  };
  onFiltroChange();
}

function getEstadoColor(estado: string) {
  const colores: Record<string, string> = {
    Draft: "warning",
    Publicado: "success",
    Finalizado: "error",
    Todos: "primary",
  };
  return colores[estado] || "grey";
}

function getSistemaColor(sistema: string) {
  const colores: Record<string, string> = {
    Torneo: "warning",
    Liga: "success",
    Todos: "primary",
  };
  return colores[sistema] || "grey";
}

function abrirNuevoTorneo() {
  torneoEditando.value = null;
  nuevoTorneo.value = {
    nombre: "",
    descripcion: "",
    genero: null,
    categoria: null,
    fecha: null,
    tipoCompetencia: null,
    valorInscripcion: "",
    sistemaCompetencia: null,
    deporte: null,
  };
  modalNuevoVisible.value = true;
  proxy.$refs.formTorneo.resetValidation();
}

async function editarTorneo(torneo: any) {
  torneoEditando.value = torneo;

  const params = {
    idTorneo: torneo.idTorneo,
  };

  const { data } = await axios.get("/Torneos/GetTorneoById", {
    params,
  });

  nuevoTorneo.value = {
    ...data,
    fecha: moment(data.fecha).format("YYYY-MM-DD"),
  };
  modalNuevoVisible.value = true;
}

async function editarPuntos(item: any) {
  const params = {
    idTorneo: item.idTorneo,
  };
  const { data } = await axios.get("/Ranking/GetRanking", {
    params,
  });

  if (!data.jugadores) {
    showAlert({
      icon: "warning",
      title: "No hay jugadores asignados para esta competencia.",
      timer: 2500,
      vm: proxy,
    });
    return;
  }

  jugadoresFinales.value = JSON.parse(data.jugadores);
  idRanking.value = data.idRanking;

  editedItemFinaliza.value = {
    idTorneo: item.idTorneo,
    nombreTorneo: item.nombre,
    tipoCompetencia: item.sistemaCompetencia,
    generoTorneo: item.genero,
    categoriaTorneo: item.categoria,
    deporte: item.deporte,
  };
  modalFinalizarVisible.value = true;
}

async function actualizarPuntos(jugadores: JugadorParaFinalizar[]) {
  try {
    const payload = {
      idTorneo: editedItemFinaliza.value.idTorneo,
      jugadores: JSON.stringify(
        jugadores.map((j) => ({
          idJugador: j.idJugador,
          nombreJugador: j.nombreJugador,
          posicion: j.ordenMerito,
          puntosAsignados: j.puntosAsignados,
        })),
      ),
      idInstitucion: proxy.$store.state.usuario.institucion.idInstitucion,
      idRanking: idRanking.value,
    };

    await axios.post("/Ranking/ModificarRanking", payload);

    modalFinalizarVisible.value = false;

    showAlert({
      icon: "success",
      title: "¡Puntos actualizados correctamente!",
      text: "Los puntos han sido asignados correctamente.",
      timer: 3000,
      vm: proxy,
    });
  } catch (error) {
    showAlert({
      icon: "error",
      title: "Error",
      text: "No se pudo actualizar los puntos.",
      timer: 4000,
      vm: proxy,
    });
  }
}

function inscripciones(torneo: any) {
  editedItem.value.idTorneo = torneo.idTorneo;
  editedItem.value.infoTorneo = `${torneo.nombre} - Deporte: ${torneo.deporte} - Categoría: ${torneo.categoria} - Género: ${torneo.genero}`;
  editedItem.value.sistemaCompetencia = torneo.sistemaCompetencia;
  modalInscripciones.value = true;
}

async function guardarTorneo() {
  if (moment(nuevoTorneo.value.fecha).isBefore(moment("1900-01-01"))) {
    showAlert({
      icon: "warning",
      title: "La fecha debe ser mayor o igual al 01/01/1900.",
      timer: 2500,
      vm: proxy,
    });
    return;
  }
  try {
    const idInstitucion = proxy.$store.state.usuario.institucion.idInstitucion;

    guardando.value = true;

    if (nuevoTorneo.value.deporte === "Futbol") {
      nuevoTorneo.value.categoria = "No aplica";
      nuevoTorneo.value.tipoCompetencia = "No aplica";
    }

    const torneoData = {
      ...nuevoTorneo.value,
      configuraciones: "",
      idInstitucion,
      estado: "Draft",
    };

    let response;

    if (torneoEditando.value) {
      response = await axios.post("/Torneos/ModificarDatosTorneo", torneoData);
    } else {
      response = await axios.post("/Torneos/GuardarTorneo", torneoData);
    }

    if (response.data) {
      showAlert({
        icon: "success",
        title: `Competición ${
          torneoEditando.value ? "editada" : "creada"
        } exitosamente.`,
        timer: 2500,
        vm: proxy,
      });

      if (!torneoEditando.value && response.data) {
        // Redirigir a la configuración del torneo recién creado
        if (nuevoTorneo.value.sistemaCompetencia === "Torneo") {
          router.push(`/torneos/${idInstitucion}/${response.data}`);
        } else {
          router.push(`/ligas/${idInstitucion}/${response.data}`);
        }
      } else {
        cargarTorneos();
      }
      cerrarModalNuevo();
    }
  } catch (error) {
    showAlert({
      icon: "warning",
      title:
        "No se pudo guardar la competición. Intente nuevamente más tarde por favor.",
      timer: 2500,
      vm: proxy,
    });
    console.error(error);
  } finally {
    guardando.value = false;
  }
}

function cerrarModalNuevo() {
  modalNuevoVisible.value = false;
  torneoEditando.value = null;
}

function abrirTorneo(
  id: number,
  idInstitucion: number,
  sistemaCompetencia: string,
) {
  if (sistemaCompetencia === "Torneo") {
    router.push(`/torneos/${idInstitucion}/${id}`);
  } else {
    router.push(`/ligas/${idInstitucion}/${id}`);
  }
}

async function eliminarTorneo(idTorneo: number) {
  const result = await confirmModal({
    vm: proxy,
    title: "Eliminar competición.",
    text: "¿Está seguro que desea eliminar la competición? Esta acción no se puede deshacer.",
    icon: "warning",
    showCancelButton: true,
    showCloseButton: true,
  });

  if (!result.isConfirmed) {
    return;
  }

  try {
    await axios.get("/Torneos/EliminarTorneo", {
      params: {
        idTorneo,
      },
    });

    showAlert({
      icon: "success",
      title: "Competición eliminada exitosamente.",
      timer: 2500,
      vm: proxy,
    });
    cargarTorneos();
  } catch (error) {
    showAlert({
      icon: "warning",
      title: "La competición no pudo ser eliminada.",
      timer: 2500,
      vm: proxy,
    });
    console.error(error);
  }
}

async function cambiarEstado(idTorneo: any, estado: string) {
  if (estado === "Finalizado") {
    const result = await confirmModal({
      vm: proxy,
      title: "Finalizar competición.",
      text: "¿Está seguro que desea finalizar la competición? Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      showCloseButton: true,
    });

    if (!result.isConfirmed) {
      return;
    }
  }
  const datosTorneo = {
    nombre: "",
    descripcion: "",
    genero: null,
    categoria: null,
    fecha: "01/01/2025",
    tipoCompetencia: null,
    estado,
    configuraciones: "",
    idTorneo,
    idInstitucion: proxy.$store.state.usuario.institucion.idInstitucion,
  };
  try {
    await axios.post("/Torneos/CambiarEstado", datosTorneo);
    cargarTorneos();
  } catch (error) {
    console.log(error);
  }
}

function refresh() {
  cargarTorneos();
}

function setearFechaInicial() {
  const fechaInicio = moment()
    .subtract(4, "month")
    .startOf("month")
    .format("YYYY-MM-DD");

  const fechaFin = moment().add(1, "month").endOf("month").format("YYYY-MM-DD");
  filtros.value.fecha = [fechaInicio, fechaFin];
}
// Watchers
watch(
  opcionesTabla,
  () => {
    cargarTorneos();
  },
  { deep: true },
);

watch(
  () => filtros.value.fecha,
  (val) => {
    if (val && val.length === 2) {
      moment.locale("fr");
      fechaFormateada.value =
        moment(val[0]).format("L") + " - " + moment(val[1]).format("L");
      menu.value = false;
    }
  },
);

// Lifecycle
onMounted(() => {
  setearFechaInicial();
  cargarTorneos();
});

return { __sfc: true,proxy, menu, fechaFormateada, modalFinalizarVisible, jugadoresFinales, idRanking, cargando, torneos, filtros, opcionesTabla, cantRegistros, valorInscripcionRules, modalNuevoVisible, modalInscripciones, formValido, guardando, torneoEditando, nuevoTorneo, editedItem, editedItemFinaliza, estados, sistemas, sistemasFiltros, estadosFiltros, generos, generosFiltros, tiposCompetencia, categoriasModel, categoriasFiltros, deportesModel, deportesFiltros, headers, footerProps, snackbar, filtrosActivos, volver, toFixed, onlyNumber, cargarTorneos, onFiltroChangeDebounced, onFiltroChange, quitarFiltro, limpiarFiltros, getEstadoColor, getSistemaColor, abrirNuevoTorneo, editarTorneo, editarPuntos, actualizarPuntos, inscripciones, guardarTorneo, cerrarModalNuevo, abrirTorneo, eliminarTorneo, cambiarEstado, refresh, setearFechaInicial, ListaInscriptos, ModalFinalizarCompetencia }
}

})